/** Person, party and procurement dossiers. */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { CompanyList } from '../components/lists';
import { DataTable } from '../components/DataTable';
import {
  Amount, Badge, Breadcrumb, DetailHead, Disclosure, ErrorState, Fact, GraphButton, Link, Loading,
  SectionHead,
} from '../components/ui';
import {
  date, holding, mandateTerm, nominationFor, num, shortHash, truncate, validity,
} from '../lib/format';
import { downloadXlsx, sheetOf, stampedFilename } from '../lib/xlsx';

/* --------------------------------------------------------------- person */

const PERSON = graphql(`
  query PersonDossier($hash: ID!) {
    people(where: { person_hash: $hash }) {
      person_hash
      name
      ownedCompaniesConnection {
        edges {
          properties { share_percent share_amount share_currency valid_from valid_to }
          node { uic name tendersWonAggregate { count } }
        }
      }
      managedCompaniesConnection {
        edges { properties { role valid_from valid_to } node { uic name } }
      }
      mayorOfConnection {
        edges { properties { elected_year term_end elected_round } node { key name province } }
      }
      # Elected office below mayor of a municipality: кмет на кметство and
      # общински съветник. Absent from this page until now, so a village
      # mayor's dossier said nothing about the office they hold — the one
      # fact that makes the rest of the page worth reading.
      positionsConnection(sort: [{ edge: { elected_year: DESC } }]) {
        edges {
          properties { role area elected_year term_end list_letter preferences replaces }
          node { key name province }
        }
      }
      nominatedByConnection { edges { properties { election_year } node { key name } } }
    }
  }
`);

export function PersonPage({ hash }: { hash: string }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['person', hash],
    queryFn: () => request(PERSON, { hash }),
  });
  if (isPending) return <Loading />;
  if (isError) return <ErrorState message={(error).message} />;
  const person = data.people[0];
  if (!person) return <ErrorState message="Лицето не е намерено в графа." />;

  const mandates = [...person.mayorOfConnection.edges]
    .sort((a, b) => (b.properties.elected_year ?? 0) - (a.properties.elected_year ?? 0));
  /* Newest first, and the office named — a person can be a village mayor
     in one term and a councillor in the next, and until now the dossier
     showed neither. */
  const positions = [...person.positionsConnection.edges]
    .sort((a, b) => (b.properties.elected_year ?? 0) - (a.properties.elected_year ?? 0));
  const owned = person.ownedCompaniesConnection.edges;
  const managed = person.managedCompaniesConnection.edges;

  return (
    <>
      <Breadcrumb>{[
        <Link key="up" href="#/people">Лица</Link>,
        person.name ?? shortHash(hash),
      ]}</Breadcrumb>

      <DetailHead
        eyebrow="Лице"
        title={person.name ?? 'Без име'}
        meta={[['Идентификатор', <span className="mono">{shortHash(hash)}</span>] as const]}
        actions={(
          <>
            <GraphButton href={`#/graph?person=${encodeURIComponent(hash)}`} />
            {' '}
            <GraphButton href={`#/activity?person=${encodeURIComponent(hash)}`}>
              Карта на дейността
            </GraphButton>
          </>
        )}
      />

      <div className="facts">
        <Fact value={num(owned.length)} label="Дялове във фирми" />
        <Fact value={num(managed.length)} label="Управлява" />
        {mandates.length > 0 && <Fact value={num(mandates.length)} label="Кметски мандати" />}
        {positions.length > 0 && (
          <Fact value={num(positions.length)} label="Изборни длъжности" />
        )}
      </div>

      {mandates.length > 0 && (
        <section className="block">
          <SectionHead title="Кметски мандати" aside="по данни на ЦИК" />
          <DataTable
            rows={mandates}
            rowKey={(edge) => `${edge.node.key}:${edge.properties.elected_year}`}
            columns={[
              {
                key: 'term',
                label: 'Мандат',
                render: (edge) => <span className="mono">{mandateTerm(edge.properties)}</span>,
              },
              {
                key: 'municipality',
                label: 'Община',
                render: (edge) => (
                  <Link href={`#/municipalities/${encodeURIComponent(edge.node.key)}`}>
                    {edge.node.name}
                  </Link>
                ),
              },
              { key: 'province', label: 'Област', render: (edge) => edge.node.province ?? '—' },
              {
                key: 'round',
                label: 'Избран',
                render: (edge) => (edge.properties.elected_round === 2 ? 'на балотаж' : 'на първи тур'),
              },
              {
                key: 'now',
                label: '',
                render: (edge) => (edge.properties.term_end == null
                  ? <Badge kind="municipality">настоящ</Badge> : null),
              },
            ]}
          />
        </section>
      )}

      {positions.length > 0 && (
        <section className="block">
          <SectionHead
            title="Изборни длъжности"
            aside="кметства и общински съвети, по данни на ЦИК"
          />
          <DataTable
            rows={positions}
            rowKey={(edge) => `${edge.node.key}:${edge.properties.role}`
              + `:${edge.properties.area}:${edge.properties.elected_year}`}
            columns={[
              {
                key: 'role',
                label: 'Длъжност',
                render: (edge) => (
                  <div>
                    {edge.properties.role ?? 'изборна длъжност'}
                    {/* A village mayor's area is their settlement, which is
                        the whole point of the office; a councillor's is the
                        municipality and would only repeat the next column. */}
                    {edge.properties.role !== 'общински съветник' && edge.properties.area && (
                      <div className="uic">{edge.properties.area}</div>
                    )}
                  </div>
                ),
              },
              {
                key: 'municipality',
                label: 'Община',
                render: (edge) => (
                  <Link href={`#/municipalities/${encodeURIComponent(edge.node.key)}`}>
                    {edge.node.name}
                  </Link>
                ),
              },
              {
                key: 'term',
                label: 'Мандат',
                render: (edge) => (
                  <span className="mono">
                    {edge.properties.term_end
                      ? `${edge.properties.elected_year} – ${edge.properties.term_end}`
                      : `${edge.properties.elected_year} –`}
                  </span>
                ),
              },
              {
                key: 'note',
                label: '',
                render: (edge) => {
                  if (edge.properties.term_end == null) {
                    return <Badge kind="municipality">настоящ</Badge>;
                  }
                  return edge.properties.replaces
                    ? (
                      <span className="uic">
                        {`на мястото на ${truncate(edge.properties.replaces, 24)}`}
                      </span>
                    )
                    : null;
                },
              },
            ]}
          />
        </section>
      )}

      {/* One nomination per election, so a change of banner is visible
          rather than overwritten. */}
      {person.nominatedByConnection.edges.length > 0 && (
        <section className="block">
          <SectionHead title="Издигнат от" />
          <DataTable
            rows={[...person.nominatedByConnection.edges]
              .sort((a, b) => (b.properties.election_year ?? 0) - (a.properties.election_year ?? 0))}
            rowKey={(edge) => `${edge.node.key}:${edge.properties.election_year}`}
            columns={[
              {
                key: 'year',
                label: 'Избори',
                render: (edge) => edge.properties.election_year ?? '—',
              },
              {
                key: 'party',
                label: 'Партия / инициативен комитет',
                render: (edge) => (
                  <Link href={`#/parties/${encodeURIComponent(edge.node.key)}`}>
                    {truncate(edge.node.name, 60)}
                  </Link>
                ),
              },
            ]}
          />
        </section>
      )}

      {owned.length > 0 && (
        <section className="block">
          <SectionHead title="Дялове във фирми" />
          <DataTable
            rows={owned}
            rowKey={(edge) => edge.node.uic}
            columns={[
              {
                key: 'company',
                label: 'Фирма',
                render: (edge) => (
                  <Link href={`#/companies/${edge.node.uic}`}>
                    {edge.node.name ?? edge.node.uic}
                  </Link>
                ),
              },
              { key: 'share', label: 'Дял', num: true, render: (edge) => holding(edge.properties) },
              { key: 'period', label: 'Период', render: (edge) => validity(edge.properties) },
              {
                key: 'tenders',
                label: 'Поръчки',
                num: true,
                render: (edge) => num(edge.node.tendersWonAggregate?.count),
              },
            ]}
          />
        </section>
      )}

      {managed.length > 0 && (
        <section className="block">
          <SectionHead title="Управлява" />
          <DataTable
            rows={managed}
            rowKey={(edge) => edge.node.uic}
            columns={[
              {
                key: 'company',
                label: 'Фирма',
                render: (edge) => (
                  <Link href={`#/companies/${edge.node.uic}`}>
                    {edge.node.name ?? edge.node.uic}
                  </Link>
                ),
              },
              { key: 'role', label: 'Длъжност', render: (edge) => edge.properties.role ?? '—' },
              { key: 'period', label: 'Период', render: (edge) => validity(edge.properties) },
            ]}
          />
        </section>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- party */

const PARTY = graphql(`
  query PartyDossier($key: ID!, $familyKey: String!) {
    parties(where: { key: $key }) { key name family_key family_name }
    family: parties(where: { family_key: $familyKey }, options: { limit: 1 }) {
      family_key
      family_name
    }
    electionYears
  }
`);

/* Membership and succession edges around this dossier's scope — authored
   through the admin service, shown here so a coalition's record is one
   click from its members and vice versa. */
const PARTY_STRUCTURE = graphql(`
  query PartyStructure($key: ID!, $family: String!) {
    parties(where: { OR: [{ key: $key }, { family_key: $family }] }) {
      key
      name
      memberOfConnection {
        edges { properties { from_year to_year note } node { key name } }
      }
      membersConnection {
        edges { properties { from_year to_year note } node { key name } }
      }
      succeededByConnection { edges { properties { year note } node { key name } } }
      succeedsConnection { edges { properties { year note } node { key name } } }
    }
  }
`);

/* The same view for one ballot name or for every name of the party. Both are
   offered because they answer different questions, and the difference is
   large: БСП under one banner shows 1.79bn лв across 34 municipalities,
   while the family — 40 ballot names — shows 3.13bn across 57. */
const PARTY_SUPPLIERS = graphql(`
  query PartySuppliersFor($party: ID, $family: String, $withCoalitions: Boolean) {
    partyProcurement(party: $party, family: $family, withCoalitions: $withCoalitions) {
      name count total debt
    }
    partySuppliers(party: $party, family: $family, limit: 25,
                   withCoalitions: $withCoalitions) {
      uic name awards value municipalities shareOfParty shareOfCompany
    }
  }
`);

/* Councils, one row per municipality rather than one per councillor: a
   party with 1,441 seats has a list nobody reads, while 244 councils
   ordered by how much of each it holds is its local footprint. */
const PARTY_COUNCILS = graphql(`
  query PartyCouncilsFor($party: ID, $family: String, $year: Int, $withCoalitions: Boolean) {
    partyCouncils(party: $party, family: $family, electionYear: $year,
                  withCoalitions: $withCoalitions) {
      municipality municipalityKey province population electedYear party
      seats councilSize leads majority mayorIsOurs mayorParty mayorIndependent
    }
  }
`);

const PARTY_MAYORS = graphql(`
  query PartyMayorsFor($party: ID, $family: String, $year: Int, $withCoalitions: Boolean) {
    partyMayors(party: $party, family: $family, electionYear: $year,
                withCoalitions: $withCoalitions) {
      person_hash name party municipality municipality_key province population
      elected_year elected_round term_end
    }
  }
`);

/* Mayors are asked for one election at a time. A party that stood in three
   elections holds three mandates per municipality it kept, and listing them
   together read as three times as many mayors — and summed the population
   it governs three times over. */
export function PartyPage({ partyKey }: { partyKey: string }) {
  const [year, setYear] = useState<number | null>(null);
  const [wholeFamily, setWholeFamily] = useState(true);
  /* Coalition records are the coalition's. This switch adds them to the
     reading, labelled — it never folds them in silently. */
  const [withCoalitions, setWithCoalitions] = useState(false);

  const meta = useQuery({
    queryKey: ['party', partyKey],
    queryFn: () => request(PARTY, { key: partyKey, familyKey: partyKey }),
  });

  const years = meta.data?.electionYears ?? [];
  const selected = year ?? years[0] ?? null;

  const banner = meta.data?.parties[0] ?? null;
  /* The grouped list links by the family key, which no Party node carries
     as its own `key`; a miss on the exact lookup means this page is the
     whole family, resolved through any of its members. */
  const familyRow = !banner ? meta.data?.family[0] ?? null : null;

  const family = familyRow ? partyKey : banner?.family_key ?? null;
  // one of the two is always null; typed explicitly so the union does not
  // narrow to whichever branch is written first
  const scope: { party: string | null; family: string | null } =
    familyRow || (wholeFamily && family)
      ? { party: null, family }
      : { party: partyKey, family: null };

  const structure = useQuery({
    queryKey: ['party-structure', partyKey],
    queryFn: () => request(PARTY_STRUCTURE, {
      key: partyKey, family: family ?? partyKey,
    }),
    enabled: Boolean(meta.data),
  });

  const supply = useQuery({
    queryKey: ['party-supply', scope.party, scope.family, withCoalitions],
    queryFn: () => request(PARTY_SUPPLIERS, { ...scope, withCoalitions }),
    enabled: Boolean(meta.data),
  });

  // a banner page lists the banner's own mayors whatever the scope toggle
  // says; a family page has no single banner to fall back to
  const mayorScope: { party: string | null; family: string | null } = familyRow
    ? { party: null, family: partyKey }
    : { party: partyKey, family: null };
  const mayors = useQuery({
    queryKey: ['party-mayors', mayorScope.party, mayorScope.family, selected, withCoalitions],
    queryFn: () => request(PARTY_MAYORS, { ...mayorScope, year: selected, withCoalitions }),
    enabled: selected != null,
  });

  const councils = useQuery({
    queryKey: ['party-councils', mayorScope.party, mayorScope.family, selected, withCoalitions],
    queryFn: () => request(PARTY_COUNCILS, { ...mayorScope, year: selected, withCoalitions }),
    enabled: selected != null,
  });
  /* Summed here rather than asked of the API: the rows are already on the
     page and a second aggregate would be a second round trip to count
     what is in front of us. */
  const councilTotals = (councils.data?.partyCouncils ?? []).reduce(
    (acc, row) => ({
      councils: acc.councils + 1,
      seats: acc.seats + row.seats,
      leads: acc.leads + (row.leads ? 1 : 0),
      majority: acc.majority + (row.majority ? 1 : 0),
    }),
    { councils: 0, seats: 0, leads: 0, majority: 0 },
  );

  if (meta.isPending) return <Loading />;
  if (meta.isError) return <ErrorState message={(meta.error).message} />;
  const party = meta.data.parties[0] ?? null;
  if (!party && !familyRow) {
    return <ErrorState message="Партията не е намерена в графа." />;
  }
  const title = party?.name ?? familyRow?.family_name ?? partyKey;

  const rows = mayors.data?.partyMayors ?? [];
  const suppliers = supply.data?.partySuppliers ?? [];
  const procurement = supply.data?.partyProcurement;
  const population = rows.reduce((sum, row) => sum + (row.population ?? 0), 0);
  const runoffs = rows.filter((row) => row.elected_round === 2).length;

  /* One family can hold several nodes linked to the same coalition, so
     each direction is deduped on the far key. */
  const scopeKeys = new Set((structure.data?.parties ?? []).map((node) => node.key));
  const dedupeEdges = <TProps,>(
    edges: { properties: TProps; node: { key: string; name?: string | null } }[],
  ) => [...new Map(edges
    .filter((edge) => !scopeKeys.has(edge.node.key))
    .map((edge) => [edge.node.key, edge] as const)).values()];
  const structureParties = structure.data?.parties ?? [];
  const partOf = dedupeEdges(structureParties.flatMap(
    (node) => node.memberOfConnection.edges));
  const coalitionMembers = dedupeEdges(structureParties.flatMap(
    (node) => node.membersConnection.edges));
  const succeededBy = dedupeEdges(structureParties.flatMap(
    (node) => node.succeededByConnection.edges));
  const succeeds = dedupeEdges(structureParties.flatMap(
    (node) => node.succeedsConnection.edges));
  const membershipYears = (properties: { from_year?: number | null; to_year?: number | null }) =>
    `${properties.from_year ?? '…'} – ${properties.to_year ?? 'сега'}`;

  /* The dossier as a workbook: the suppliers under the scope on screen and
     the mayors of the election on screen, with a provenance sheet naming
     both choices — the file outlives the page state that shaped it. */
  const exportXlsx = () => {
    downloadXlsx(stampedFilename(`партия-${title.replace(/[\\/:*?"<>|]/g, ' ').trim().slice(0, 40)}`), [
      sheetOf('Изпълнители', [
        { header: 'Фирма', value: (row: typeof suppliers[number]) => row.name ?? row.uic },
        { header: 'ЕИК', value: (row) => row.uic },
        { header: 'Поръчки', value: (row) => row.awards },
        { header: 'Общини', value: (row) => row.municipalities },
        { header: 'Стойност (лв.)', format: 'money', value: (row) => row.value },
        { header: 'Дял от партията', format: 'percent', value: (row) => row.shareOfParty },
        { header: 'Дял от фирмата', format: 'percent', value: (row) => row.shareOfCompany },
      ], suppliers),
      sheetOf(`Кметове ${selected ?? ''}`, [
        { header: 'Кмет', value: (row: typeof rows[number]) => row.name },
        { header: 'Издигнат от', value: (row) => row.party },
        { header: 'Идентификатор', value: (row) => row.person_hash },
        { header: 'Община', value: (row) => row.municipality },
        { header: 'Област', value: (row) => row.province },
        { header: 'Население', value: (row) => row.population },
        { header: 'Избран', value: (row) => row.elected_year },
        { header: 'Тур', value: (row) => row.elected_round },
        { header: 'Мандат до', value: (row) => row.term_end },
      ], rows),
      {
        name: 'Справка',
        header: ['Поле', 'Стойност'],
        rows: [
          ['Източник', 'Алфа-Омега — от бюджета до получателя'],
          ['Изтеглено на', new Date().toISOString()],
          ['Страница', title],
          ['Обхват', scope.family
            ? 'цялата партия — всички наименования на бюлетината'
            : 'само това наименование'],
          ['Коалиции с участие', withCoalitions ? 'включени (означено четене)' : 'не'],
          ['Лист „Кметове“ — местни избори', selected ?? '—'],
          ['Поръчки под администрациите', procurement?.count ?? 0],
          ['Стойност (лв.)', procurement?.total ?? 0],
          ['Лист „Изпълнители“', `най-големите ${suppliers.length}; пълният списък е в регистър „Поръчки“`],
        ],
      },
    ]);
  };

  return (
    <>
      <Breadcrumb>{[
        <Link key="up" href="#/parties">Партии</Link>,
        title,
      ]}</Breadcrumb>

      <DetailHead
        eyebrow={party ? 'Партия / инициативен комитет' : 'Партия — групирани наименования'}
        title={title}
        meta={[['Издигнати кметове', String(rows.length)] as const]}
        actions={(
          <button
            type="button"
            className="btn"
            onClick={exportXlsx}
            disabled={supply.isPending || mayors.isPending}
            title="Изтегля изпълнителите и кметовете при избраните обхват и избори"
          >
            Експорт XLSX
          </button>
        )}
      />

      {years.length > 1 && (
        <div className="facet-row">
          <label className="facet-label" htmlFor="election-year">Местни избори</label>
          <select
            className="facet-select"
            id="election-year"
            value={String(selected ?? '')}
            onChange={(event) => setYear(Number(event.target.value))}
          >
            {years.map((option) => (
              <option key={option} value={String(option)}>{option} г.</option>
            ))}
          </select>
          <span className="facet-note">
            {selected === years[0] ? 'настоящ мандат' : 'приключил мандат'}
          </span>
        </div>
      )}

      <div className="facts">
        <Fact value={num(rows.length)} label="Кметове" />
        <Fact value={num(population)} label="Управлявано население" />
        <Fact value={num(runoffs)} label="Избрани на балотаж" />
      </div>

      {/* Who is paid under this party's administrations.
          Two ratios, because either alone misleads: the share of the party's
          spending says how much of its money one company takes, while the
          share of the company's own municipal work says whether that company
          depends on this party or merely appears in a large party's column.
          A municipal transport operator reads 100% across one municipality
          and says nothing about politics. */}
      {party?.family_name && (
        <div className="facet-row">
          <label className="facet-label" htmlFor="party-scope">Обхват</label>
          <select
            className="facet-select"
            id="party-scope"
            value={wholeFamily ? 'family' : 'banner'}
            onChange={(event) => setWholeFamily(event.target.value === 'family')}
          >
            <option value="family">{`Цялата партия — ${party.family_name}`}</option>
            <option value="banner">{`Само това наименование — ${party.name}`}</option>
          </select>
          <span className="facet-note">
            партия се явява на избори под различни наименования; групирането е ръчно
            изготвено и не покрива местни формации и независими кандидати
          </span>
        </div>
      )}

      {partOf.length > 0 && (
        <div className="facet-row">
          <label className="facet-check">
            <input
              type="checkbox"
              checked={withCoalitions}
              onChange={(event) => setWithCoalitions(event.target.checked)}
            />
            <span>включително коалициите с участие</span>
          </label>
          <span className="facet-note">
            записът на коалицията си остава неин — включването е означено четене по
            членство, валидно за изборите на съответната номинация, не пренасяне
          </span>
        </div>
      )}

      {(partOf.length > 0 || coalitionMembers.length > 0
        || succeededBy.length > 0 || succeeds.length > 0) && (
        <section className="block">
          <SectionHead title="Структура и приемственост"
            aside="въведени твърдения, с бележка към всяко" />
          <div className="grid grid-2">
            {partOf.length > 0 && (
              <div>
                <h3 className="eyebrow">Част от</h3>
                {partOf.map((edge) => (
                  <p key={edge.node.key} style={{ margin: '.25rem 0' }}>
                    <Link href={`#/parties/${encodeURIComponent(edge.node.key)}`}>
                      {truncate(edge.node.name ?? edge.node.key, 52)}
                    </Link>
                    <span className="uic mono">{` ${membershipYears(edge.properties)}`}</span>
                    {edge.properties.note ? <span className="uic">{` · ${edge.properties.note}`}</span> : null}
                  </p>
                ))}
              </div>
            )}
            {coalitionMembers.length > 0 && (
              <div>
                <h3 className="eyebrow">Обединява</h3>
                {coalitionMembers.map((edge) => (
                  <p key={edge.node.key} style={{ margin: '.25rem 0' }}>
                    <Link href={`#/parties/${encodeURIComponent(edge.node.key)}`}>
                      {truncate(edge.node.name ?? edge.node.key, 52)}
                    </Link>
                    <span className="uic mono">{` ${membershipYears(edge.properties)}`}</span>
                  </p>
                ))}
              </div>
            )}
            {succeeds.length > 0 && (
              <div>
                <h3 className="eyebrow">Продължение на</h3>
                {succeeds.map((edge) => (
                  <p key={edge.node.key} style={{ margin: '.25rem 0' }}>
                    <Link href={`#/parties/${encodeURIComponent(edge.node.key)}`}>
                      {truncate(edge.node.name ?? edge.node.key, 52)}
                    </Link>
                    {edge.properties.year != null && (
                      <span className="uic mono">{` от ${edge.properties.year}`}</span>
                    )}
                    {edge.properties.note ? <span className="uic">{` · ${edge.properties.note}`}</span> : null}
                  </p>
                ))}
              </div>
            )}
            {succeededBy.length > 0 && (
              <div>
                <h3 className="eyebrow">Наследена от</h3>
                {succeededBy.map((edge) => (
                  <p key={edge.node.key} style={{ margin: '.25rem 0' }}>
                    <Link href={`#/parties/${encodeURIComponent(edge.node.key)}`}>
                      {truncate(edge.node.name ?? edge.node.key, 52)}
                    </Link>
                    {edge.properties.year != null && (
                      <span className="uic mono">{` от ${edge.properties.year}`}</span>
                    )}
                    {edge.properties.note ? <span className="uic">{` · ${edge.properties.note}`}</span> : null}
                  </p>
                ))}
              </div>
            )}
          </div>
          <p className="colophon-note">
            Приемствеността е частично продължение, не тъждество, затова никоя сума
            не я следва автоматично. Членството е ограничено по избирателни години —
            СДС в Реформаторския блок и СДС до ГЕРБ са различни периоди и не се смесват.
          </p>
        </section>
      )}

      {(procurement?.count ?? 0) > 0 && suppliers.length > 0 && procurement && (
        <section className="block">
          <SectionHead
            title="Изпълнители под администрациите на партията"
            aside={(
              <>
                {num(procurement.count)} поръчки · <Amount value={procurement.total ?? 0} />
                {' · '}{num(Math.round(procurement.debt ?? 0))} общини
                {withCoalitions && ' · включително коалиции'}
              </>
            )}
          />
          <p className="page-lede" style={{ marginBottom: '1rem' }}>
            Поръчки, възложени от общини, докато кмет, издигнат от тази партия, е бил на
            власт — по датата на възлагане, не по днешното управление. Съсредоточаването
            само по себе си не е нарушение: голяма партия управлява голяма част от страната,
            затова колоната „от фирмата“ е тази, която отличава изпълнител, зависим от една
            партия, от такъв, който просто присъства в колоната на голяма партия.
          </p>
          <DataTable
            rows={suppliers}
            rowKey={(row) => row.uic}
            columns={[
              {
                key: 'name',
                label: 'Фирма',
                render: (row) => (
                  <div>
                    <Link href={`#/companies/${row.uic}`}>{row.name ?? row.uic}</Link>
                    <div className="uic mono">
                      {row.uic} · {row.awards} поръчки · {row.municipalities} общини
                    </div>
                  </div>
                ),
              },
              {
                key: 'value',
                label: 'Стойност',
                num: true,
                render: (row) => <Amount value={row.value} />,
              },
              {
                key: 'ofParty',
                label: 'От партията',
                num: true,
                render: (row) => `${(row.shareOfParty * 100).toFixed(1)}%`,
              },
              {
                key: 'ofCompany',
                label: 'От фирмата',
                num: true,
                render: (row) => (row.shareOfCompany == null
                  ? '—'
                  : (
                    <span className={row.shareOfCompany >= 0.9 && row.municipalities > 1
                      ? 'debt' : 'amount'}>
                      {`${(row.shareOfCompany * 100).toFixed(0)}%`}
                    </span>
                  )),
              },
            ]}
          />
          {party && (
            <p className="colophon-note">
              Местните коалиции са отделни партии в графа по замисъл — „ПП ГЕРБ“ и
              „МК ГЕРБ (СДС)“ не са едно и също тяло — затова тази страница показва само
              едната формация. Партия, чиито кметове са избирани под различни коалиционни
              имена, е разпределена между няколко страници.
            </p>
          )}
        </section>
      )}

      {councils.isPending ? <Loading /> : (
        <Disclosure
          title="Общински съвети"
          aside={councilTotals.councils
            ? `${num(councilTotals.councils)} съвета · ${num(councilTotals.seats)} съветници`
            : 'няма съветници'}
          defaultOpen={councilTotals.councils > 0}
        >
          <p className="dossier-prose">
            Кметът е изпълнителната власт; бюджетът и разходите се приемат от
            общинския съвет. Затова местата тук казват колкото и кметските
            мандати — а къде партията води съвет без мнозинство, всяко решение
            минава през договорка с друга група.
            {councilTotals.majority > 0 && ` Мнозинство има в ${num(councilTotals.majority)} `
              + `от ${num(councilTotals.leads)} водени съвета.`}
          </p>
          <DataTable
            rows={councils.data?.partyCouncils ?? []}
            rowKey={(row) => `${row.municipalityKey}:${row.electedYear}`}
            empty={`Партията няма избрани съветници на местните избори ${selected} г.`}
            columns={[
              {
                key: 'municipality',
                label: 'Община',
                render: (row) => (
                  <div>
                    <Link href={`#/municipalities/${encodeURIComponent(row.municipalityKey)}`}>
                      {row.municipality}
                    </Link>
                    <div className="uic">{row.province ?? ''}</div>
                  </div>
                ),
              },
              {
                /* Seats against the size of the council, because 8 of 17
                   and 8 of 51 are not the same fact — and the bar makes
                   the difference legible without arithmetic. */
                key: 'seats',
                label: 'Места',
                num: true,
                render: (row) => (
                  <div>
                    <span className="amount">
                      {num(row.seats)}
                      <span className="muted">{` / ${num(row.councilSize)}`}</span>
                    </span>
                    <div className="seat-bar" aria-hidden="true">
                      <span style={{ width: `${(100 * row.seats) / Math.max(1, row.councilSize)}%` }} />
                    </div>
                  </div>
                ),
              },
              {
                /* Leading and controlling are different things, and the
                   table says which this is rather than leaving the reader
                   to divide. */
                key: 'standing',
                label: 'Положение',
                render: (row) => {
                  if (row.majority) return <Badge kind="party">мнозинство</Badge>;
                  if (row.leads) return <span>води без мнозинство</span>;
                  return <span className="muted">в опозиция на водещата група</span>;
                },
              },
              {
                key: 'mayor',
                label: 'Кмет',
                render: (row) => {
                  if (row.mayorIsOurs) return <span>на партията</span>;
                  // An инициативен комитет is keyed as a party named after
                  // its candidate; printing that name here would look like
                  // a party and read as a bug.
                  if (row.mayorIndependent) {
                    return (
                      <span className="muted" title={row.mayorParty ?? undefined}>
                        независим
                      </span>
                    );
                  }
                  return (
                    <span className="muted" title={row.mayorParty ?? undefined}>
                      {row.mayorParty ? truncate(row.mayorParty, 26) : '—'}
                    </span>
                  );
                },
              },
              {
                key: 'population',
                label: 'Население',
                num: true,
                render: (row) => num(row.population),
              },
            ]}
          />
        </Disclosure>
      )}

      {mayors.isPending ? <Loading /> : (
        <section className="block">
          <SectionHead title="Кметове"
            aside={withCoalitions ? 'включително коалиции с участие' : undefined} />
          <DataTable
            rows={rows}
            rowKey={(row) => `${row.municipality_key}:${row.elected_year}`}
            empty={`Партията няма избрани кметове на местните избори ${selected} г.`}
            columns={[
              {
                key: 'mayor',
                label: 'Кмет',
                render: (row) => (
                  <div>
                    <Link href={`#/people/${row.person_hash}`}>{row.name}</Link>
                    {withCoalitions && row.party && (
                      <div className="uic mono">{truncate(row.party, 42)}</div>
                    )}
                  </div>
                ),
              },
              {
                key: 'municipality',
                label: 'Община',
                render: (row) => (
                  <Link href={`#/municipalities/${encodeURIComponent(row.municipality_key)}`}>
                    {row.municipality}
                  </Link>
                ),
              },
              { key: 'province', label: 'Област', render: (row) => row.province ?? '—' },
              {
                key: 'round',
                label: 'Избран',
                render: (row) => (row.elected_round === 2 ? 'на балотаж' : 'на първи тур'),
              },
              {
                key: 'population',
                label: 'Население',
                num: true,
                render: (row) => num(row.population),
              },
            ]}
          />
        </section>
      )}
    </>
  );
}

/* ------------------------------------------------------------- tender */

const TENDER = graphql(`
  query TenderDossier($id: ID!) {
    tenders(where: { tender_id: $id }) {
      tender_id title procuring_entity cpv_code cpv_description publication_date
      awarded_amount currency winner_count lot_count awarded_total_bgn estimated_value
      value_bgn value_flag value_flag_reason value_ratio value_excluded
      duplicate_of
      alsoPublishedAs { tender_id title }
      publishedAlsoIn { tender_id }
      procedure_type contract_type buyer_type buyer_activity award_method
      legal_basis is_eu_funded european_program is_framework is_accelerated is_green
      is_cancelled submission_deadline execution_nuts link_to_ojeu
      has_subcontractors subcontractor_name subcontracting_percent
      offers_count sme_offers_count disqualified_offers_count direct_award_justification
      lots {
        lot_key lot_id contract_number awarded_amount currency winners { uic name }
      }
      commissionedBy {
        key name province
        mayorsConnection {
          edges {
            properties { elected_year term_end elected_round }
            node {
              person_hash name
              nominatedByConnection { edges { properties { election_year } node { key name } } }
            }
          }
        }
      }
      winnersConnection {
        edges {
          properties { awarded_amount }
          node { uic name taxDebtsAggregate { count } seatedIn { key name } }
        }
      }
    }
  }
`);

/* The mandate covering a date.

   A procurement is awarded under whoever was mayor at the time, which is
   why the mandates are interval-stamped: showing today's mayor beside a
   2020 contract would attribute it to the wrong person. A contract
   predating every loaded election is attributed to nobody. */
function mandateAt<T extends { properties: { elected_year?: number | null; term_end?: number | null } }>(
  edges: readonly T[], isoDate?: string | null,
): T | null {
  const year = Number(String(isoDate ?? '').slice(0, 4));
  if (!Number.isFinite(year)) return null;
  return edges.find((edge) => {
    const from = edge.properties.elected_year;
    const to = edge.properties.term_end;
    return from != null && year >= from && (to == null || year < to);
  }) ?? null;
}

export function TenderPage({ id }: { id: string }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['tender', id],
    queryFn: () => request(TENDER, { id }),
  });
  if (isPending) return <Loading />;
  if (isError) return <ErrorState message={(error).message} />;
  const tender = data.tenders[0];
  if (!tender) return <ErrorState message="Поръчката не е намерена в графа." />;

  const buyer = tender.commissionedBy[0];
  const mandate = mandateAt(buyer?.mayorsConnection.edges ?? [], tender.publication_date);
  const mayor = mandate?.node;
  const party = nominationFor(mandate);
  const winners = tender.winnersConnection.edges;
  const multiLot = (tender.lot_count ?? 0) > 1;

  const terms: [string, string | number | null | undefined][] = [
    ['Вид на договора', tender.contract_type],
    ['Метод на оценка', tender.award_method],
    ['Правно основание', tender.legal_basis],
    ['Вид възложител', tender.buyer_type],
    ['Дейност на възложителя', tender.buyer_activity],
    ['Краен срок за оферти', tender.submission_deadline ? date(tender.submission_deadline) : null],
    ['Място на изпълнение (NUTS)', tender.execution_nuts],
    ['Програма на ЕС', tender.european_program],
    ['Подизпълнител', tender.subcontractor_name],
    ['Дял на подизпълнителя', tender.subcontracting_percent],
    ['Обосновка за директно възлагане', tender.direct_award_justification],
  ];
  const shown = terms.filter(([, value]) => value != null && value !== '');

  return (
    <>
      <Breadcrumb>{[
        <Link key="up" href="#/tenders">Поръчки</Link>,
        tender.tender_id,
      ]}</Breadcrumb>

      <DetailHead
        eyebrow="Обществена поръчка"
        title={tender.title ?? tender.tender_id}
        compact={(tender.title ?? '').length > 90}
        meta={[
          ['Номер', <span className="mono">{tender.tender_id}</span>] as const,
          tender.procuring_entity
            ? ['Възложител', truncate(tender.procuring_entity, 44)] as const : null,
          tender.cpv_code ? ['CPV', tender.cpv_description
            ? `${tender.cpv_code} · ${truncate(tender.cpv_description, 38)}`
            : tender.cpv_code] as const : null,
          tender.procedure_type ? ['Процедура', tender.procedure_type] as const : null,
          ['Публикувана', date(tender.publication_date)] as const,
        ]}
        actions={winners.length > 0
          ? (
            <GraphButton href={`#/graph?tender=${encodeURIComponent(tender.tender_id)}`}>
              {winners.length > 1 ? `Граф на ${winners.length}-те изпълнителя` : 'Граф на изпълнителя'}
            </GraphButton>
          )
          : undefined}
      />

      <div className="facts">
        <Fact
          value={multiLot && tender.awarded_total_bgn != null
            ? <Amount value={tender.awarded_total_bgn} />
            : <Amount value={tender.awarded_amount} currency={tender.currency} />}
          label={multiLot ? 'Възложено (сбор от позициите)' : 'Възложена стойност'}
        />
        {tender.estimated_value != null && (
          <Fact value={<Amount value={tender.estimated_value} currency={tender.currency} />} label="Прогнозна стойност" />
        )}
        <Fact value={num(winners.length)} label="Изпълнители" />
        {tender.offers_count != null && (
          <Fact value={num(tender.offers_count)}
            label={tender.offers_count === 1 ? 'Оферта (единствена)' : 'Получени оферти'} />
        )}
        {tender.is_eu_funded != null && (
          <Fact value={tender.is_eu_funded ? 'Да' : 'Не'} label="Финансиране от ЕС" />
        )}
        {mayor && (
          <Fact
            value={<Link href={`#/people/${mayor.person_hash}`}>{truncate(mayor.name, 22)}</Link>}
            label={`Кмет ${mandateTerm(mandate?.properties)}`}
          />
        )}
        {party && (
          <Fact
            value={(
              <Link href={`#/parties/${encodeURIComponent(party.key)}`}>
                {truncate(party.name, 22)}
              </Link>
            )}
            label="Партия"
          />
        )}
      </div>

      {/* A published amount exactly 100x its own estimate is a dropped
          decimal separator. Both figures are shown as published; what is
          withheld is the comparable value used in totals. */}
      {tender.value_flag && (
        <p className="page-lede" style={{ margin: '-.8rem 0 2rem' }}>
          <Badge kind={tender.value_excluded ? 'debt' : 'municipality'}>
            {tender.duplicate_of
              ? 'второ публикуване'
              : (tender.value_excluded ? 'извън общите суми' : 'разминаване в стойността')}
          </Badge>{' '}
          {tender.value_flag_reason}{' '}
          {/* The national record is where the winners, the buyer's ЕИК and
              the procedure live — this page deliberately has none of them,
              so it has to say where they went. */}
          {tender.alsoPublishedAs[0]
            ? (
              <Link href={`#/tenders/${encodeURIComponent(tender.alsoPublishedAs[0].tender_id)}`}>
                Към записа в ЦАИС ЕОП →
              </Link>
            )
            : <Link href="#/observations">Всички такива случаи →</Link>}
        </p>
      )}

      {/* The reverse, on the record that survived: the reader is entitled
          to know the contract was announced in Europe too, and to check
          the notice for themselves. */}
      {tender.publishedAlsoIn.length > 0 && (
        <p className="page-lede" style={{ margin: '-.8rem 0 2rem' }}>
          <Badge kind="municipality">публикувана и в TED</Badge>{' '}
          {'Договорът е обявен и в Официален вестник на ЕС. Обявлението е същата '
            + 'поръчка, не втора — стойността се брои веднъж, тук.'}{' '}
          <Link href={`#/tenders/${encodeURIComponent(tender.publishedAlsoIn[0]!.tender_id)}`}>
            {tender.publishedAlsoIn[0]!.tender_id} →
          </Link>
        </p>
      )}

      {winners.length > 1 && (
        <p className="page-lede" style={{ margin: '-.8rem 0 2rem' }}>
          <Badge kind="municipality">разделена поръчка</Badge>{' '}
          {`Обявлението обхваща ${tender.lot_count ?? 'няколко'} обособени позиции и `}
          {`${winners.length} изпълнители. Публикуваната стойност е за поръчката като цяло — `}
          източникът не разпределя суми по изпълнители, затова тук не се приписват.
          Няколко изпълнители по едно обявление означава разделена поръчка, а не обединение.
        </p>
      )}

      {shown.length > 0 && (
        <section className="block">
          <SectionHead title="Условия на процедурата" aside="по данни на ЦАИС ЕОП" />
          <dl className="detail-meta detail-meta-wide">
            {shown.map(([term, value]) => (
              <div key={term}><dt>{term}</dt><dd>{String(value)}</dd></div>
            ))}
          </dl>
        </section>
      )}

      {tender.lots.length > 0 && (
        <section className="block">
          <SectionHead title="Обособени позиции" aside={String(tender.lots.length)} />
          <DataTable
            rows={tender.lots}
            rowKey={(lot) => lot.lot_key}
            columns={[
              { key: 'lot', label: 'Позиция', render: (lot) => lot.lot_id ?? lot.lot_key },
              { key: 'no', label: 'Договор', render: (lot) => lot.contract_number ?? '—' },
              {
                key: 'winners',
                label: 'Изпълнител(и)',
                render: (lot) => lot.winners.map((winner, index) => (
                  <span key={winner.uic}>
                    {index > 0 && ', '}
                    <Link href={`#/companies/${winner.uic}`}>
                      {truncate(winner.name ?? winner.uic, 26)}
                    </Link>
                  </span>
                )),
              },
              {
                key: 'amount',
                label: 'Стойност',
                num: true,
                render: (lot) => <Amount value={lot.awarded_amount} currency={lot.currency} />,
              },
            ]}
          />
        </section>
      )}

      {winners.length > 0 && (
        <section className="block">
          <SectionHead title="Изпълнители" />
          <DataTable
            rows={winners}
            rowKey={(edge) => edge.node.uic}
            columns={[
              {
                key: 'company',
                label: 'Фирма',
                render: (edge) => (
                  <div>
                    <Link href={`#/companies/${edge.node.uic}`}>
                      {edge.node.name ?? edge.node.uic}
                    </Link>
                    <div className="uic mono">{edge.node.uic}</div>
                  </div>
                ),
              },
              {
                key: 'seat',
                label: 'Седалище',
                render: (edge) => edge.node.seatedIn[0]?.name ?? '—',
              },
              {
                key: 'flags',
                label: '',
                render: (edge) => (edge.node.taxDebtsAggregate?.count
                  ? <Badge kind="debt">{edge.node.taxDebtsAggregate.count} задълж.</Badge>
                  : null),
              },
              {
                key: 'share',
                label: 'Стойност',
                num: true,
                render: (edge) => (edge.properties.awarded_amount != null
                  ? <Amount value={edge.properties.awarded_amount} currency={tender.currency} />
                  : <span className="muted">не се публикува по изпълнител</span>),
              },
            ]}
          />
        </section>
      )}

      {buyer && (
        <CompanyList
          prefix="c."
          scope={{ seatKeys: [buyer.key] }}
          heading={`Други фирми със седалище в ${buyer.name}`}
        />
      )}

      {tender.link_to_ojeu && (
        <p className="colophon-note">
          <a className="rowlink" href={tender.link_to_ojeu} target="_blank" rel="noreferrer">
            Обявлението в Официалния вестник на ЕС ↗
          </a>
        </p>
      )}
    </>
  );
}
